/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 09-09-2026
 *
 * De tweede baan van het protocol: de simulatie. Zelfde vorm als `actietabel.ts` — per actie wat de
 * rollen ermee doen — maar met een eigen kanaal (`simulatiebericht`) en een paar eigen regels:
 *
 * - **Eén actie per bericht**, geen reeks; de simulatie synchroniseert per gebaar.
 * - **De logregels gaan direct naar het backend**, niet in de buffer: tijdens een simulatie wordt er
 *   niet opgeslagen, en een gebufferde regel zou pas veel later of nooit vertrekken.
 * - **Geen undo en geen save.** De simulatie verandert het model niet.
 *
 * Vier acties hangen aan het simulatiescherm zelf (openen, sluiten, een stap zetten, het snelste pad).
 * Ze lopen via de geregistreerde `SimulatieAansturing` van de `SimulatieService`.
 */
import type {Actieregel, Actieargumenten} from './actietabel';
import type {Simulatie_eiland} from './protocol.types';
import type {SimulatieAansturing} from './samenwerkprotocol.service';

/**
 * Wat de simulatietabel nodig heeft. Het eiland kan er nog niet zijn — dan is er ook niets te
 * simuleren en doet de regel niets.
 */
export interface Simulatieomgeving {
    eiland: () => Simulatie_eiland | null;
    aansturing: () => SimulatieAansturing | null;
    /** Neemt een binnengekomen simulatiestatus van de leider over. */
    neemStatusOver: (status: unknown) => void;
    /** Neemt een binnengekomen simfeedback-status van de leider over. */
    neemSimfeedbackOver: (status: unknown) => void;
    /** Verwerkt als leider een simfeedback-verzoek van een volger. */
    voerSimfeedbackactieUit: (actie: string, arg: unknown, volger?: string) => void;
    /** Zet de vlag voor het snelste pad, zoals de leider hem meldt. */
    neemSnelstePadOver: (waarde: boolean) => void;
    /** Neemt over dat de uitvoerder op een simulatie wacht, of niet meer — en of dat gestopt is. */
    neemBezigOver: (bezig: boolean, gestopt: boolean) => void;
}

/**
 * Bouwt de simulatietabel.
 */
export function maakSimulatietabel(omgeving: Simulatieomgeving): Map<string, Actieregel> {
    const {eiland, aansturing, neemStatusOver, neemSimfeedbackOver, voerSimfeedbackactieUit, neemSnelstePadOver,
        neemBezigOver} = omgeving;

    /**
     * Een gebaar dat de leider uitvoert op zijn simcanvas en dat in de actielog hoort. De volger
     * vraagt het; de leider logt op diens naam en doet het.
     * @param naam de actienaam op de lijn
     * @param logactie de naam van de actielogregel
     * @param uitvoeren wat het simcanvas ermee doet
     */
    const canvasgebaar = (naam: string, logactie: string,
                          uitvoeren: (canvas: Simulatie_eiland, volger?: string) => void): Actieregel => ({
        vraag: () => ({actie: naam}),
        lees: () => ({}),
        uitvoer: (_args, volger) => {
            const canvas = eiland();
            if (!canvas) {
                return {};
            }
            uitvoeren(canvas, volger ?? undefined);
            return {directlog: {actie: logactie, payload: {}, volger, doel: 'model'}};
        }
    });

    return new Map<string, Actieregel>([

        /* ******** Het simulatiescherm ******** */

        ['fullOrStop', {
            vraag: () => ({actie: 'fullOrStop'}),
            lees: () => ({}),
            uitvoer: (_args, volger) => {
                aansturing()?.startOfStop(volger ?? undefined);
                return {}; //het loggen zit in de aansturing: het snapshotmoment verschilt per richting
            }
        }],

        ['nextstep', {
            vraag: () => ({actie: 'nextstep'}),
            lees: () => ({}),
            uitvoer: (_args, volger) => {
                aansturing()?.volgendeStap(volger ?? undefined);
                return {};
            }
        }],

        ['niceStates', {
            vraag: () => ({actie: 'niceStates'}),
            lees: () => ({}),
            uitvoer: (_args, volger) => {
                aansturing()?.mooieStates(volger ?? undefined);
                return {};
            }
        }],

        ['toggleFastestPath', {
            vraag: () => ({actie: 'toggleFastestPath'}),
            lees: () => ({}),
            uitvoer: () => {
                aansturing()?.wisselSnelstePad();
                return {};
            }
        }],

        ['SetFastestPath', {
            //de leider heeft het snelste pad om- of uitgezet; bij ons is het puur weergave
            vanLeider: stap => {
                neemSnelstePadOver(!!stap['fastest_path']);
                return {};
            }
        }],

        /* ******** De toestandsknoppen ******** */

        ['selectAllstates', canvasgebaar('selectAllstates', 'sim_selectall',
            (canvas, volger) => canvas.selecteerAlleStates(volger))],
        ['selectNone', canvasgebaar('selectNone', 'sim_selectnone',
            (canvas, volger) => canvas.selecteerGeenStates(volger))],
        ['selectEndings', canvasgebaar('selectEndings', 'sim_selectendings',
            (canvas, volger) => canvas.selecteerEindStates(volger))],
        ['selectPad', canvasgebaar('selectPad', 'sim_selectpad',
            (canvas, volger) => canvas.selecteerPad(volger))],

        ['setSelected', {
            //het canvas meldt zijn eigen selectie via `SimulatieActies`; hier komt alleen de kant
            //binnen waarin een volger het de leider vraagt
            lees: stap => ({selected: stap['selected'], selectievolgorde: stap['selectievolgorde']}),
            uitvoer: (args, volger) => {
                eiland()?.pasSelectieToe({
                    selectievolgorde: args['selectievolgorde'] as number,
                    selected: args['selected']
                }, volger ?? undefined);
                return {};
            }
        }],

        /* ******** De histories ******** */

        ['toggleValueHistory', canvasgebaar('toggleValueHistory', 'sim_toggle_vh',
            canvas => canvas.toggleAlleValuehistory())],
        ['toggleIneqHistory', canvasgebaar('toggleIneqHistory', 'sim_toggle_ih',
            canvas => canvas.toggleAlleIneqhistory())],
        ['snapStates', canvasgebaar('snapStates', 'snap_states',
            canvas => canvas.doLayout())],

        ['hideValueHistory', {
            lees: stap => ({historyNode: stap['historyNode']}),
            uitvoer: args => {
                eiland()?.verbergValuehistoryVanaf(args['historyNode'] as string);
                return {};
            }
        }],

        ['hideIneqHistory', {
            lees: stap => ({ineqkey: stap['ineqkey']}),
            uitvoer: args => {
                eiland()?.verbergIneqhistoryVanaf(args['ineqkey'] as string);
                return {};
            }
        }],

        /* ******** De stand van het simulatiescherm ******** */

        ['positie', {
            //de leider past toe, stuurt door en logt op naam van de volger die versleepte
            lees: stap => ({posities: stap['posities']}),
            uitvoer: (args, volger) => {
                eiland()?.pasPositiesToe(args['posities'] as Record<string, {x: number; y: number}>);
                return {
                    simbericht: {actie: 'positie', args: {posities: args['posities']}},
                    directlog: {actie: 'sim_position', payload: {}, volger, doel: 'sim'}
                };
            },
            vanLeider: stap => {
                eiland()?.pasPositiesToe(stap['posities'] as Record<string, {x: number; y: number}>);
                return {};
            }
        }],

        ['simstatus', {
            //de volledige stand van de leider: simulatie, canvas, histories en simfeedback
            vanLeider: stap => {
                neemStatusOver(stap['status']);
                return {};
            }
        }],

        ['simbezig', {
            //de uitvoerder wacht op een simulatie, of is klaar met wachten (golf 3.6). Zonder dit ziet
            //een volger niets van een rekenende leider tot het resultaat er ineens staat
            vanLeider: stap => {
                neemBezigOver(!!stap['bezig'], !!stap['gestopt']);
                return {};
            }
        }],

        /* ******** De simfeedback ******** */

        ['simfeedbackactie', {
            lees: stap => ({feedbackactie: stap['feedbackactie'], feedbackarg: stap['feedbackarg']}),
            uitvoer: (args, volger) => {
                voerSimfeedbackactieUit(args['feedbackactie'] as string, args['feedbackarg'],
                    volger ?? undefined);
                return {};
            }
        }],

        ['simfeedback', {
            vanLeider: stap => {
                neemSimfeedbackOver(stap['status']);
                return {};
            }
        }]
    ]);
}

/**
 * De argumenten waarmee de simulatiebaan zijn acties aanroept; dezelfde vorm als bij de modelkant.
 */
export type Simulatieargumenten = Actieargumenten;
